import { translate } from '@core/translate';
import { AbstractInput } from '@rxdi/forms';
import { Component, css, html, LitElement, unsafeCSS } from '@rxdi/lit-html';
import { FormDefinition } from '@shared/components';
import { notify } from '@shared/notify';
import { SteelFabTheme } from '@shared/styles/theme';
import * as ContactComponentCSS from 'bundle-text:./contact.component.css';

interface ContactForm {
  name: string;
  email: string;
  company: string;
  message: string;
}

/**
 * @customElement contact-component
 */
@Component({
  selector: 'contact-component',
  styles: [SteelFabTheme, unsafeCSS(ContactComponentCSS)],
  template(this: ContactComponent) {
    return html`
      <section class="section" id="contact-component">
        <div class="container">
          <div class="inner">
            <div>
              <h2 class="left-title">Свържете се с нас</h2>
              <p class="left-sub">
                Готови сме да отговорим на всички ваши въпроси и да изготвим индивидуална оферта за вашите нужди.
              </p>
              <div class="form-card">
                ${this.sent ? html`<div class="success-msg">✅ Отворихме чернова във вашия пощенски клиент.</div>` : ''}

                <h3 class="form-title">Изпратете запитване</h3>

                <dynamic-form
                  .definition=${this.getFormDefinition()}
                  .layout=${true}
                  .errorTemplate=${(input: AbstractInput) => {
                    console.log(input.validationMessage);
                    if (input && input.touched && !input.validity.valid) {
                      const message = input.validationMessage;
                      if ('classList' in input) {
                        input.classList.add('is-invalid');
                      }
                      return html`
                        <style>
                          .validation-error {
                            color: #a94442;
                            font-size: 12px;
                            padding-top: 5px;
                          }
                        </style>
                        <span class="validation-error" part="error-message">${translate(message)}</span>
                      `;
                    }
                    if (input && 'classList' in input) {
                      input.classList?.remove('is-invalid');
                    }
                    return '';
                  }}
                  @submit=${(e: CustomEvent<ContactForm>) => this.submitForm(e.detail)}
                >
                  <button slot="submit" type="submit" class="submit-btn">Изпрати запитване →</button>
                </dynamic-form>
              </div>
              <div class="contact-info">
                <div>
                  <div class="contact-row-inline">
                    <div>
                      <h3>Офис София</h3>
                      <div class="contact-row">
                        <div class="contact-icon">📍</div>
                        <span>бл.418, ж.к. Младост 4, 1715 София</span>
                      </div>
                      <div class="contact-row">
                        <div class="contact-icon">🕐</div>
                        <span>Пон – Пет: 08:00–18:00</span>
                      </div>
                      <div class="contact-row">
                        <div class="contact-icon">📞</div>
                        <a href="tel:+359885647622">+359/885-647-622</a>
                      </div>
                    </div>
                    <div class="map-embed">
                      <iframe
                        title="Steel Fab location"
                        loading="lazy"
                        referrerpolicy="no-referrer-when-downgrade"
                        src="https://www.google.com/maps?q=%D0%B1%D0%BB.418,%20%D0%B6.%D0%BA.%20%D0%9C%D0%BB%D0%B0%D0%B4%D0%BE%D1%81%D1%82%204,%201715%20%D0%A1%D0%BE%D1%84%D0%B8%D1%8F&amp;output=embed&amp;gestureHandling=greedy"
                      ></iframe>
                    </div>
                  </div>
                </div>
                <div>
                  <div class="contact-row-inline">
                    <div>
                      <h3>Офис Стара Загора</h3>
                      <div class="contact-row">
                        <div class="contact-icon">📍</div>
                        <span>бл.418, ж.к. Младост 4, 1715 София</span>
                      </div>
                      <div class="contact-row">
                        <div class="contact-icon">🕐</div>
                        <span>Пон – Пет: 08:00 – 18:00</span>
                      </div>
                      <div class="contact-row">
                        <div class="contact-icon">📞</div>
                        <a href="tel:+359885647622">+359/885-647-622</a>
                      </div>
                    </div>
                    <div class="map-embed">
                      <iframe
                        title="Steel Fab location"
                        loading="lazy"
                        referrerpolicy="no-referrer-when-downgrade"
                        src="https://www.google.com/maps?q=%D0%B1%D0%BB.418,%20%D0%B6.%D0%BA.%20%D0%9C%D0%BB%D0%B0%D0%B4%D0%BE%D1%81%D1%82%204,%201715%20%D0%A1%D0%BE%D1%84%D0%B8%D1%8F&amp;output=embed&amp;gestureHandling=greedy"
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class ContactComponent extends LitElement {
  name = '';
  email = '';
  company = '';
  message = '';
  sent = false;

  submitForm(formData?: ContactForm | null) {
    console.log(formData);

    const recipient = 'nikolay.ivanov@steelfab.bg';
    const subject = formData?.company
      ? `Запитване от ${formData.name} (${formData.company})`
      : `Запитване от ${formData?.name}`;
    const body = [formData?.message].join('\n');

    const mailtoUrl = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoUrl;
    this.name = '';
    this.email = '';
    this.company = '';
    this.message = '';
    notify.success('Отворихме чернова във вашия пощенски клиент.');
  }

  private getFormDefinition(): FormDefinition {
    return {
      name: 'productForm',
      fields: [
        {
          name: 'name',
          type: 'text',
          label: 'Имена *',
          placeholder: 'Въведете вашите имена',
          validators: [{ name: 'required' }],
        },
        {
          name: 'company',
          type: 'text',
          label: 'Фирма',
          placeholder: 'Въведете името на вашата фирма',
        },
        {
          name: 'email',
          type: 'text',
          label: 'Имейл *',
          placeholder: 'Въведете вашия имейл',
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
          patternError: 'Моля, въведете валиден имейл адрес',
          validators: [{ name: 'required' }],
        },
        {
          name: 'message',
          type: 'textarea',
          label: 'Съобщение *',
          placeholder: 'Въведете вашето съобщение',
          validators: [{ name: 'required' }],
        },
      ],
    };
  }
}
